import api from "./client";

/**
 * Types pour l'estimation de coûts
 */

export interface ResourceCostRequest {
  id: string;
  type: string;
  params?: Record<string, any>;
}

export interface CostBreakdownItem {
  ok: boolean;
  resourceId: string;
  resourceType: string;
  name: string;
  sku: string;
  region: string;
  provider: string;
  monthly: number;
  hourly: number;
  currency: string;
  breakdown?: Record<string, any>;
  metadata?: Record<string, any>;
}

export interface UnsupportedResource {
  resourceId: string;
  resourceType: string;
  error: string;
  detail?: string;
}

export interface CostEstimationResult {
  ok: boolean;
  totalMonthly: number;
  totalHourly: number;
  currency: string;
  breakdown: CostBreakdownItem[];
  unsupported: UnsupportedResource[];
  updatedAt: string;
  disclaimer?: string;
}

export interface PricingSKU {
  name: string;
  serviceName: string;
  regions: Record<string, {
    hourly: number | null;
    monthly: number | null;
    unit: string;
    currency: string;
  }>;
  metadata?: Record<string, any>;
}

export interface PricingDataResponse {
  ok: boolean;
  provider: string;
  resourceType: string;
  skus: PricingSKU[];
}

export interface PricingStats {
  ok: boolean;
  totalSKUs: number;
  byProvider: {
    azure?: number;
    aws?: number;
    gcp?: number;
  };
  lastRefresh: string | null;
  nextRefresh: string | null;
}

/**
 * Estime le coût d'une architecture complète
 * @param resources - Liste des ressources à estimer
 * @param currency - Devise (USD par défaut)
 * @param architectureId - ID de l'architecture (optionnel, pour résolution des variables)
 */
export async function estimateCost(
  resources: ResourceCostRequest[],
  currency: string = 'USD',
  architectureId?: string
): Promise<CostEstimationResult> {
  const response = await api.post("/api/cost/estimate", {
    resources,
    currency,
    architectureId
  });
  return response.data;
}

/**
 * Estime le coût d'une seule ressource
 */
export async function estimateResourceCost(
  resource: ResourceCostRequest
): Promise<CostBreakdownItem> {
  const response = await api.post("/api/cost/estimate-resource", {
    resource
  });
  return response.data;
}

/**
 * Récupère tous les SKUs disponibles pour un type de ressource
 */
export async function getPricingData(
  provider: 'azure' | 'aws' | 'gcp',
  resourceType: string
): Promise<PricingDataResponse> {
  const response = await api.get(`/api/cost/pricing/${provider}/${resourceType}`);
  return response.data;
}

/**
 * Récupère les statistiques sur le cache de pricing
 */
export async function getPricingStats(): Promise<PricingStats> {
  const response = await api.get("/api/cost/stats");
  return response.data;
}

/**
 * Déclenche manuellement le refresh du cache (admin uniquement)
 */
export async function refreshPricingCache(provider?: 'azure' | 'aws' | 'gcp'): Promise<any> {
  const response = await api.post("/api/cost/refresh-cache", { provider });
  return response.data;
}

/**
 * Obtient les recommandations d'optimisation des coûts
 */
export async function getOptimizationRecommendations(
  resources: ResourceCostRequest[],
  options?: {
    includeReservedInstances?: boolean;
    includeSpotInstances?: boolean;
  }
): Promise<any> {
  const response = await api.post("/api/cost/optimize", {
    resources,
    options
  });
  return response.data;
}

/**
 * Exporte l'estimation de coûts en PDF
 */
export async function exportCostPDF(
  resources: ResourceCostRequest[],
  options?: {
    projectName?: string;
    companyName?: string;
    currency?: string;
  }
): Promise<void> {
  const response = await api.post("/api/cost/export/pdf", {
    resources,
    options
  }, {
    responseType: 'blob'
  });

  // Créer un lien de téléchargement
  const blob = new Blob([response.data], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `cost-estimation-${Date.now()}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * Exporte l'estimation de coûts en Excel
 */
export async function exportCostExcel(
  resources: ResourceCostRequest[],
  options?: {
    projectName?: string;
    currency?: string;
  }
): Promise<void> {
  const response = await api.post("/api/cost/export/excel", {
    resources,
    options
  }, {
    responseType: 'blob'
  });

  // Créer un lien de téléchargement
  const blob = new Blob([response.data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `cost-estimation-${Date.now()}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

// ==================== Historique des estimations ====================

export interface SavedCostEstimation {
  id: string;
  projectId?: number;
  projectName: string;
  userId?: number;
  totalMonthly: number;
  totalHourly: number;
  currency: string;
  breakdown?: CostBreakdownItem[];
  resources?: ResourceCostRequest[];
  unsupported?: UnsupportedResource[];
  budget?: number;
  budgetAlertThreshold?: number;
  notes?: string;
  tags?: string[];
  architectureSnapshot?: any;
  isArchived: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SaveEstimationRequest {
  projectId?: number;
  projectName: string;
  totalMonthly: number;
  totalHourly: number;
  currency?: string;
  breakdown?: CostBreakdownItem[];
  resources?: ResourceCostRequest[];
  unsupported?: UnsupportedResource[];
  budget?: number;
  budgetAlertThreshold?: number;
  notes?: string;
  tags?: string[];
  architectureSnapshot?: any;
}

export interface EstimationListResponse {
  ok: boolean;
  data: SavedCostEstimation[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface EstimationStats {
  count: number;
  totalCost: number;
  averageCost: number;
  minCost: number;
  maxCost: number;
  budgetAlerts: number;
}

/**
 * Sauvegarde une estimation de coûts
 */
export async function saveCostEstimation(data: SaveEstimationRequest): Promise<SavedCostEstimation> {
  const response = await api.post("/api/cost/history", data);
  return response.data.data;
}

/**
 * Récupère toutes les estimations
 */
export async function getCostEstimations(params?: {
  page?: number;
  limit?: number;
  projectId?: number;
  userId?: number;
  isArchived?: boolean;
  search?: string;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}): Promise<EstimationListResponse> {
  const response = await api.get("/api/cost/history", { params });
  return response.data;
}

/**
 * Récupère une estimation par ID
 */
export async function getCostEstimationById(id: string): Promise<SavedCostEstimation> {
  const response = await api.get(`/api/cost/history/${id}`);
  return response.data.data;
}

/**
 * Met à jour une estimation
 */
export async function updateCostEstimation(
  id: string,
  updates: Partial<SaveEstimationRequest>
): Promise<SavedCostEstimation> {
  const response = await api.put(`/api/cost/history/${id}`, updates);
  return response.data.data;
}

/**
 * Supprime une estimation
 */
export async function deleteCostEstimation(id: string): Promise<void> {
  await api.delete(`/api/cost/history/${id}`);
}

/**
 * Archive une estimation
 */
export async function archiveCostEstimation(id: string): Promise<SavedCostEstimation> {
  const response = await api.post(`/api/cost/history/${id}/archive`);
  return response.data.data;
}

/**
 * Compare plusieurs estimations
 */
export async function compareEstimations(ids: string[]): Promise<any> {
  const response = await api.post("/api/cost/history/compare", { ids });
  return response.data.data;
}

/**
 * Récupère les statistiques des estimations
 */
export async function getEstimationStats(params?: {
  projectId?: number;
  userId?: number;
}): Promise<EstimationStats> {
  const response = await api.get("/api/cost/history/stats", { params });
  return response.data.data;
}

// ==================== FinOps Multi-niveaux ====================

export interface ArchitectureCost {
  id: string;
  name: string;
  projectId?: string;
  projectName?: string;
  provider: string;
  monthly: number;
  hourly: number;
  resourceCount: number;
  breakdown?: CostBreakdownItem[];
  unsupported?: UnsupportedResource[];
  error?: string;
}

export interface ProjectCost {
  id: string;
  name: string;
  provider: string;
  architecturesCount: number;
  monthly: number;
  hourly: number;
  architectures: ArchitectureCost[];
}

export interface GlobalCostsResponse {
  ok: boolean;
  level: 'global';
  currency: string;
  totalMonthly: number;
  totalHourly: number;
  projectsCount: number;
  architecturesCount: number;
  projectCosts: ProjectCost[];
  updatedAt: string;
}

export interface ProjectCostsResponse {
  ok: boolean;
  level: 'project';
  currency: string;
  projectId: string;
  projectName: string;
  provider: string;
  totalMonthly: number;
  totalHourly: number;
  architecturesCount: number;
  architectureCosts: ArchitectureCost[];
  updatedAt: string;
}

/**
 * Récupère les coûts globaux de toutes les architectures
 */
export async function getGlobalCosts(currency: string = 'USD'): Promise<GlobalCostsResponse> {
  const response = await api.get("/api/finops/global", { params: { currency } });
  return response.data;
}

/**
 * Récupère les coûts d'un projet spécifique
 */
export async function getProjectCosts(projectId: string, currency: string = 'USD'): Promise<ProjectCostsResponse> {
  const response = await api.get(`/api/finops/project/${projectId}`, { params: { currency } });
  return response.data;
}

/**
 * Récupère les coûts d'une architecture spécifique
 */
export async function getArchitectureCosts(architectureId: string, currency: string = 'USD'): Promise<any> {
  const response = await api.get(`/api/finops/estimate-architecture/${architectureId}`, { params: { currency } });
  return response.data;
}
